import { computeProfitAndLoss, monthlyProfitAndLoss, revenueOf, weekdayPerformance, type FinanceInput } from './finance';
import type { Period } from './period';

/** El mismo resultado contable que la P&L, preparado para una lectura visual. */
export function businessOverview(input: FinanceInput, period: Period) {
  const pnl = computeProfitAndLoss(input, period);
  const selected = input.revenues.filter((row) => row.date >= period.start && row.date <= period.end);
  const byDate = new Map<string, number>();
  for (const row of selected) byDate.set(row.date, (byDate.get(row.date) ?? 0) + revenueOf(row));
  const dates = [...byDate.keys()].sort();
  // Include observed activity even outside the configured schedule. Without a
  // schedule the existing recorded-day behavior is preserved.
  const operatedDates = new Set(dates);
  const configured = (input.locations ?? []).filter(location => location.active && !!location.operatingWeekdays?.length);
  for (const location of configured) {
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: location.timezone ?? 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    const end = period.end < today ? period.end : today;
    for (let date = new Date(`${period.start}T00:00:00Z`); date.toISOString().slice(0, 10) <= end; date.setUTCDate(date.getUTCDate() + 1)) {
      const weekday = ((date.getUTCDay() + 6) % 7) + 1;
      if (location.operatingWeekdays!.includes(weekday)) operatedDates.add(date.toISOString().slice(0, 10));
    }
  }
  const monthly = (Date.parse(period.end) - Date.parse(period.start)) / 86400_000 > 90;
  const series = monthly
    ? monthlyProfitAndLoss(input, period).map((row) => ({ date: row.periodStart, revenue: row.revenue }))
    : dates.map((date) => ({ date, revenue: byDate.get(date)! }));
  const costs = [
    { key: 'food', label: 'Materia prima', amount: pnl.materialConsumption },
    { key: 'labor', label: 'Personal', amount: pnl.laborCost },
    { key: 'operating', label: 'Otros gastos operativos', amount: pnl.operatingExpenses },
    { key: 'other', label: 'Amortización, intereses e impuestos', amount: pnl.depreciation + pnl.financialExpenses + pnl.taxes },
  ];
  return {
    revenue: pnl.revenue, netProfit: pnl.netProfit, netProfitPct: pnl.netProfitPct,
    ebitda: pnl.ebitda, hasInventory: pnl.hasInventory, costs,
    recordedDays: dates.length, lastDate: dates.at(-1),
    operatedDays: operatedDates.size, hasOperatingSchedule: configured.length > 0,
    hasCosts: costs.some((cost) => cost.amount !== 0),
    series, granularity: monthly ? 'month' as const : 'day' as const,
    weekday: weekdayPerformance(selected, period).map((row) => ({ ...row,
      // Consolidado: dos locales en una misma fecha cuentan como un día.
      days: [...operatedDates].filter((date) => ((new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7) + 1 === row.weekday).length,
    })),
  };
}
export type BusinessOverview = ReturnType<typeof businessOverview>;
